import { db } from './database';
import { Wheel } from '../types';
import { generateUniqueId } from '../utils/random';

export async function getAllWheels(): Promise<Wheel[]> {
  return await db.wheels.orderBy('createdAt').toArray();
}

export async function getWheelById(id: string): Promise<Wheel | undefined> {
  return await db.wheels.get(id);
}

export async function createWheel(
  name: string,
  initialEntries: string[] = ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
  colorThemeId: string = 'vibrant'
): Promise<Wheel> {
  const now = Date.now();
  const wheelId = generateUniqueId();
  const wheel: Wheel = {
    id: wheelId,
    name: name.trim() || 'Untitled Wheel',
    colorThemeId,
    spinDuration: 5,
    removeWinnerAfterSpin: false,
    createdAt: now,
    updatedAt: now,
  };

  await db.transaction('rw', db.wheels, db.entries, async () => {
    await db.wheels.add(wheel);
    const entryItems = initialEntries.map((label, idx) => ({
      id: generateUniqueId(),
      wheelId,
      label: label.trim(),
      position: idx,
      weight: 1,
      createdAt: now + idx,
    }));
    await db.entries.bulkAdd(entryItems);
  });

  return wheel;
}

export async function updateWheel(id: string, updates: Partial<Omit<Wheel, 'id' | 'createdAt'>>): Promise<void> {
  await db.wheels.update(id, {
    ...updates,
    updatedAt: Date.now(),
  });
}

export async function duplicateWheel(sourceWheelId: string): Promise<Wheel | null> {
  const sourceWheel = await db.wheels.get(sourceWheelId);
  if (!sourceWheel) return null;

  const sourceEntries = await db.entries.where('wheelId').equals(sourceWheelId).sortBy('position');
  const now = Date.now();
  const newWheelId = generateUniqueId();

  const newWheel: Wheel = {
    ...sourceWheel,
    id: newWheelId,
    name: `${sourceWheel.name} (Copy)`,
    createdAt: now,
    updatedAt: now,
  };

  const newEntries = sourceEntries.map((e, idx) => ({
    ...e,
    id: generateUniqueId(),
    wheelId: newWheelId,
    createdAt: now + idx,
  }));

  await db.transaction('rw', db.wheels, db.entries, async () => {
    await db.wheels.add(newWheel);
    await db.entries.bulkAdd(newEntries);
  });

  return newWheel;
}

export async function deleteWheel(wheelId: string): Promise<void> {
  await db.transaction('rw', db.wheels, db.entries, db.history, async () => {
    await db.wheels.delete(wheelId);
    await db.entries.where('wheelId').equals(wheelId).delete();
    await db.history.where('wheelId').equals(wheelId).delete();
  });
}
